/** Organisations (parties): vendors, customers, manufacturers… with contacts. */
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuth } from "@/app/auth";
import {
  type ContactIn,
  PARTY_KINDS,
  PARTY_ROLES,
  type Party,
  type PartyIn,
  type PartyPatch,
  useParties,
  useSaveParty,
} from "@/features/admin/reference/queries";
import {
  AttributeFields,
  type AttributeValues,
  compactAttributes,
  useAttributeDefinitions,
  withDefaults,
} from "@/shared/components/attribute-fields";
import { DataTable } from "@/shared/components/data-table";
import { TableToolbar } from "@/shared/components/table-toolbar";
import { FormDialog } from "@/shared/components/form-dialog";
import { ErrorState, LoadingState, PageHeader } from "@/shared/components/page";
import { useForm } from "@/shared/lib/form";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select, Textarea } from "@/shared/ui/form";
import { Badge } from "@/shared/ui/primitives";

interface PartyValues extends Record<string, unknown> {
  code: string;
  name: string;
  kind: string;
  tax_id: string;
  email: string;
  phone: string;
  website: string;
  notes: string;
  is_active: boolean;
}

const emptyContact = (): ContactIn => ({
  name: "",
  role: "",
  email: "",
  phone: "",
  is_primary: false,
  notes: "",
});

/** Party create/edit dialog including contacts. */
export function PartyDialog({
  party,
  onClose,
}: {
  party?: Party;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveParty();
  const defs = useAttributeDefinitions("party");
  const [roles, setRoles] = useState<string[]>(party?.roles ?? []);
  const [contacts, setContacts] = useState<ContactIn[]>(
    party?.contacts.map((c) => ({
      name: c.name,
      role: c.role ?? "",
      email: c.email ?? "",
      phone: c.phone ?? "",
      is_primary: c.is_primary ?? false,
      notes: c.notes ?? "",
    })) ?? [],
  );
  const [attributes, setAttributes] = useState<AttributeValues>(() =>
    withDefaults(defs.data ?? [], party?.attributes),
  );
  const initial = useMemo<PartyValues>(
    () => ({
      code: party?.code ?? "",
      name: party?.name ?? "",
      kind: party?.kind ?? "organisation",
      tax_id: party?.tax_id ?? "",
      email: party?.email ?? "",
      phone: party?.phone ?? "",
      website: party?.website ?? "",
      notes: party?.notes ?? "",
      is_active: party?.is_active ?? true,
    }),
    [party],
  );
  const form = useForm<PartyValues>({
    initial,
    schema: z.object({ name: z.string().trim().min(1, t("app.required")) }),
    idPrefix: "party",
  });
  const updateContact = (i: number, patch: Partial<ContactIn>): void =>
    setContacts(contacts.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const submit = async (): Promise<void> => {
    const ok = await form.submit(async (v) => {
      const body: PartyIn | PartyPatch = {
        ...(party ? {} : { code: v.code.trim() || null }),
        name: v.name.trim(),
        kind: v.kind,
        roles,
        tax_id: v.tax_id,
        email: v.email,
        phone: v.phone,
        website: v.website,
        notes: v.notes,
        attributes: compactAttributes(defs.data ?? [], attributes),
        is_active: v.is_active,
      };
      await save.mutateAsync({
        id: party?.id,
        body,
        contacts: contacts.filter((c) => c.name.trim()),
      });
    });
    if (ok) {
      onClose();
    }
  };
  return (
    <FormDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={party ? t("parties.edit") : t("parties.new")}
      onSubmit={submit}
      submitting={form.submitting}
      error={form.errors._}
      wide
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="party-name" label={t("app.name")} error={form.errors.name}>
          <Input {...form.field("name")} />
        </Field>
        {!party ? (
          <Field
            id="party-code"
            label={`${t("app.code")} (${t("app.optional")})`}
            hint={t("assets.codeHint")}
          >
            <Input {...form.field("code")} />
          </Field>
        ) : null}
        <Field id="party-kind" label={t("members.kind")}>
          <Select
            id="party-kind"
            value={form.values.kind}
            onChange={(e) => form.set("kind", e.target.value)}
          >
            {PARTY_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`parties.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field id="party-tax_id" label={t("parties.taxId")}>
          <Input {...form.field("tax_id")} />
        </Field>
        <Field id="party-email" label={t("auth.email")}>
          <Input type="email" {...form.field("email")} />
        </Field>
        <Field id="party-phone" label={t("parties.phone")}>
          <Input {...form.field("phone")} />
        </Field>
        <Field id="party-website" label={t("parties.website")}>
          <Input type="url" {...form.field("website")} />
        </Field>
      </div>
      <Field id="party-roles" label={t("parties.roles")} hint={t("parties.operatingUnitHint")}>
        <div className="flex flex-wrap gap-3" role="group">
          {PARTY_ROLES.map((r) => (
            <Checkbox
              key={r}
              id={`party-role-${r}`}
              label={t(`parties.roleNames.${r}`)}
              checked={roles.includes(r)}
              onChange={(e) =>
                setRoles(e.target.checked ? [...roles, r] : roles.filter((x) => x !== r))
              }
            />
          ))}
        </div>
      </Field>
      <Field id="party-notes" label={t("app.notes")}>
        <Textarea {...form.field("notes")} />
      </Field>
      {defs.data?.length ? (
        <AttributeFields
          definitions={defs.data}
          values={attributes}
          onChange={setAttributes}
          idPrefix="party-attr"
        />
      ) : null}
      <div className="space-y-2 rounded-md border p-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">{t("parties.contacts")}</h3>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setContacts([...contacts, emptyContact()])}
          >
            {t("parties.addContact")}
          </Button>
        </div>
        {contacts.map((c, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 border-t pt-2 sm:grid-cols-2">
            <Input
              aria-label={`${t("app.name")} ${i + 1}`}
              placeholder={t("app.name")}
              value={c.name}
              onChange={(e) => updateContact(i, { name: e.target.value })}
            />
            <Input
              aria-label={`${t("parties.contactRole")} ${i + 1}`}
              placeholder={t("parties.contactRole")}
              value={c.role}
              onChange={(e) => updateContact(i, { role: e.target.value })}
            />
            <Input
              aria-label={`${t("auth.email")} ${i + 1}`}
              placeholder={t("auth.email")}
              value={c.email}
              onChange={(e) => updateContact(i, { email: e.target.value })}
            />
            <Input
              aria-label={`${t("parties.phone")} ${i + 1}`}
              placeholder={t("parties.phone")}
              value={c.phone}
              onChange={(e) => updateContact(i, { phone: e.target.value })}
            />
            <div className="flex items-center gap-2">
              <Checkbox
                id={`party-contact-primary-${i}`}
                label={t("parties.primary")}
                checked={c.is_primary}
                onChange={(e) => updateContact(i, { is_primary: e.target.checked })}
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`${t("app.delete")} ${i + 1}`}
                onClick={() => setContacts(contacts.filter((_, idx) => idx !== i))}
              >
                ×
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Checkbox
        id="party-is_active"
        label={t("app.active")}
        checked={form.values.is_active}
        onChange={(e) => form.set("is_active", e.target.checked)}
      />
    </FormDialog>
  );
}

/** Parties page. */
export function PartiesPage(): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{ party?: Party } | null>(null);
  const paged = useParties({ q: search.q, role: search.type });
  const manage = can("parties.party.manage");
  const update = (patch: Partial<typeof search>): void =>
    void navigate({ to: "/admin/parties", search: { ...search, ...patch }, resetScroll: false });
  const columns: ColumnDef<Party>[] = [
    {
      id: "code",
      header: t("app.code"),
      cell: (c) => <span className="font-mono text-xs">{c.row.original.code}</span>,
    },
    {
      id: "name",
      header: t("app.name"),
      cell: (c) => (
        <span className={c.row.original.is_active ? "font-medium" : "font-medium line-through"}>
          {c.row.original.name}
        </span>
      ),
    },
    {
      id: "roles",
      header: t("parties.roles"),
      cell: (c) => (
        <span className="flex flex-wrap gap-1">
          {c.row.original.roles.map((r) => (
            <Badge key={r} tone="outline">
              {t(`parties.roleNames.${r}`)}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      id: "contact",
      header: t("parties.contacts"),
      cell: (c) =>
        c.row.original.contacts.find((x) => x.is_primary)?.name ??
        c.row.original.contacts[0]?.name ??
        c.row.original.email ??
        "—",
    },
    ...(manage
      ? [
          {
            id: "actions",
            header: t("app.actions"),
            cell: (c): ReactElement => (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDialog({ party: c.row.original })}
              >
                {t("app.edit")}
              </Button>
            ),
          } satisfies ColumnDef<Party>,
        ]
      : []),
  ];
  return (
    <div>
      <PageHeader
        title={t("parties.title")}
        subtitle={t("parties.subtitle")}
        actions={manage ? <Button onClick={() => setDialog({})}>{t("parties.new")}</Button> : null}
      />
      {dialog ? <PartyDialog party={dialog.party} onClose={() => setDialog(null)} /> : null}
      <TableToolbar
        filters={[
          {
            id: "type",
            label: t("parties.roles"),
            kind: "select",
            prominent: true,
            value: search.type,
            options: PARTY_ROLES.map((value) => ({
              value,
              label: t(`parties.roleNames.${value}`),
            })),
            onChange: (value) => update({ type: typeof value === "string" ? value : undefined }),
            onRemove: () => update({ type: undefined }),
          },
        ]}
        search={search.q}
        searchPlaceholder={t("app.search")}
        searchDescription={t("tablePilot.partySearchScope")}
        onSearchChange={(q) => update({ q })}
        onReset={() => update({ q: undefined, type: undefined })}
      />
      {paged.query.isPending ? (
        <LoadingState />
      ) : paged.query.isError ? (
        <ErrorState error={paged.query.error} onRetry={() => void paged.query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={paged.items}
          minWidth={800}
          getRowId={(p) => p.id}
          total={paged.total}
          hasMore={paged.hasMore}
          onLoadMore={paged.loadMore}
          isLoadingMore={paged.isLoadingMore}
        />
      )}
    </div>
  );
}
